import api from './api';
import {
  ChatConversation,
  ChatResponse,
  FileAttachment
} from '../types/chat';
import { auth } from '../config/firebase';

class ChatService {
  async createConversation(prompt: string, templateId?: string): Promise<ChatResponse> {
    const response = await api.post<ChatResponse>('/api/chat/conversations', {
      prompt,
      templateId
    });
    return response;
  }

  async sendMessage(conversationId: string, message: string): Promise<ChatResponse> {
    const response = await api.post<ChatResponse>('/api/chat/messages', {
      conversationId,
      message
    });
    return response;
  }

  async createConversationStream(
    prompt: string,
    templateId: string | undefined,
    onChunk: (chunk: string) => void,
    onStart: (assistantMessageId: string) => void,
    onConversationId: (conversationId: string) => void,
    attachments?: FileAttachment[],
    templateMetadata?: {
      templateTitle?: string;
      helperPrompt?: string;
      initialPrompt?: string;
    },
    projectId?: string,
    signal?: AbortSignal,
    onArtifactLimitStatus?: (status: {
      limitReached: boolean;
      currentCount: number;
      limit: number;
      upgradeRequired: boolean;
    }) => void,
    onTruncated?: (reason: string) => void
  ): Promise<{ conversationId: string; assistantMessageId: string }> {
    const token = await auth.currentUser?.getIdToken();
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    
    return new Promise((resolve, reject) => {
      let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

      // Handle abort signal
      const abortHandler = () => {
        if (reader) {
          reader.cancel().catch(() => {});
        }
        reject(new DOMException('Stream aborted', 'AbortError'));
      };

      if (signal) {
        if (signal.aborted) {
          console.error('Signal is already aborted before starting!');
          abortHandler();
          return;
        }
        signal.addEventListener('abort', abortHandler);
      }

      fetch(`${apiUrl}/api/chat/conversations/stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ prompt, templateId, attachments, templateMetadata, projectId }),
        signal
      }).then(async response => {
        if (!response.ok) {
          // Try to parse error response for limit errors and grace period errors
          try {
            const errorData = await response.json();
            if (errorData.error === 'ARTIFACT_LIMIT_REACHED' || errorData.error === 'MESSAGE_LIMIT_REACHED') {
              const error = new Error(errorData.message || 'Limit reached') as any;
              error.code = errorData.error;
              error.upgradeRequired = errorData.upgradeRequired;
              throw error;
            }
            if (errorData.error === 'GRACE_PERIOD_ACTIVE') {
              const error = new Error(errorData.message || 'Your account is in view-only mode') as any;
              error.code = errorData.error;
              error.gracePeriodEndDate = errorData.gracePeriodEndDate;
              throw error;
            }
          } catch (parseError) {
            // If parsing failed and it's not a re-thrown error, use generic message
            if ((parseError as any).code) throw parseError;
          }
          throw new Error('Failed to create conversation stream');
        }

        reader = response.body?.getReader() ?? null;
        const decoder = new TextDecoder();
        let conversationId = '';
        let assistantMessageId = '';
        let buffer = '';

        if (!reader) {
          throw new Error('No reader available');
        }

        try {
          while (true) {
            // Check if signal was aborted before reading next chunk
            if (signal?.aborted) {
              break;
            }

            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                let data: any;
                try {
                  data = JSON.parse(line.slice(6));
                } catch (parseErr) {
                  console.warn('Skipping malformed SSE line:', line, parseErr);
                  continue;
                }

                if (data.type === 'conversation_id') {
                  conversationId = data.conversationId;
                  onConversationId(conversationId);
                } else if (data.type === 'artifact_limit_status') {
                  // Notify caller about artifact limit status
                  if (onArtifactLimitStatus) {
                    onArtifactLimitStatus({
                      limitReached: data.limitReached,
                      currentCount: data.currentCount,
                      limit: data.limit,
                      upgradeRequired: data.upgradeRequired
                    });
                  }
                } else if (data.type === 'assistant_message_start') {
                  assistantMessageId = data.messageId;
                  onStart(assistantMessageId);
                } else if (data.type === 'chunk') {
                  onChunk(data.content);
                } else if (data.type === 'truncated') {
                  if (onTruncated) onTruncated(data.reason || 'unknown');
                } else if (data.type === 'done') {
                  resolve({ conversationId, assistantMessageId });
                  return;
                } else if (data.type === 'error') {
                  if (data.partial && onTruncated) onTruncated('error');
                  reject(new Error(data.error));
                  return;
                }
              }
            }
          }

          // If we broke out due to abort, reject with AbortError
          if (signal?.aborted) {
            reject(new DOMException('Stream aborted', 'AbortError'));
          } else {
            resolve({ conversationId, assistantMessageId });
          }
        } finally {
          if (signal) {
            signal.removeEventListener('abort', abortHandler);
          }
        }
      }).catch(err => {
        if (signal) {
          signal.removeEventListener('abort', abortHandler);
        }
        reject(err);
      });
    });
  }

  async sendMessageStream(
    conversationId: string,
    message: string,
    onChunk: (chunk: string, assistantMessageId: string) => void,
    onStart: (userMessageId: string, assistantMessageId: string) => void,
    attachments?: FileAttachment[],
    signal?: AbortSignal,
    onArtifactLimitStatus?: (status: {
      limitReached: boolean;
      currentCount: number;
      limit: number;
      upgradeRequired: boolean;
    }) => void,
    onTruncated?: (reason: string, assistantMessageId: string) => void
  ): Promise<void> {
    const token = await auth.currentUser?.getIdToken();
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';

    return new Promise((resolve, reject) => {
      let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

      // Handle abort signal
      const abortHandler = () => {
        if (reader) {
          reader.cancel().catch(() => {});
        }
        reject(new DOMException('Stream aborted', 'AbortError'));
      };

      if (signal) {
        if (signal.aborted) {
          console.error('Signal is already aborted before starting!');
          abortHandler();
          return;
        }
        signal.addEventListener('abort', abortHandler);
      }

      fetch(`${apiUrl}/api/chat/messages/stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ conversationId, message, attachments }),
        signal
      }).then(async response => {
        if (!response.ok) {
          // Try to parse error response for limit errors and grace period errors
          try {
            const errorData = await response.json();
            if (errorData.error === 'ARTIFACT_LIMIT_REACHED' || errorData.error === 'MESSAGE_LIMIT_REACHED') {
              const error = new Error(errorData.message || 'Limit reached') as any;
              error.code = errorData.error;
              error.upgradeRequired = errorData.upgradeRequired;
              throw error;
            }
            if (errorData.error === 'GRACE_PERIOD_ACTIVE') {
              const error = new Error(errorData.message || 'Your account is in view-only mode') as any;
              error.code = errorData.error;
              error.gracePeriodEndDate = errorData.gracePeriodEndDate;
              throw error;
            }
          } catch (parseError) {
            // If parsing failed and it's not a re-thrown error, use generic message
            if ((parseError as any).code) throw parseError;
          }
          console.error('Server error response:', response.status);
          throw new Error(`Failed to send message stream: ${response.status}`);
        }

        reader = response.body?.getReader() ?? null;
        const decoder = new TextDecoder();
        let userMessageId = '';
        let assistantMessageId = '';
        let buffer = '';

        if (!reader) {
          throw new Error('No reader available');
        }

        try {
          while (true) {
            // Check if signal was aborted before reading next chunk
            if (signal?.aborted) {
              break;
            }

            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                let data: any;
                try {
                  data = JSON.parse(line.slice(6));
                } catch (parseErr) {
                  console.warn('Skipping malformed SSE line:', line, parseErr);
                  continue;
                }

                if (data.type === 'artifact_limit_status') {
                  // Notify caller about artifact limit status
                  if (onArtifactLimitStatus) {
                    onArtifactLimitStatus({
                      limitReached: data.limitReached,
                      currentCount: data.currentCount,
                      limit: data.limit,
                      upgradeRequired: data.upgradeRequired
                    });
                  }
                } else if (data.type === 'user_message') {
                  userMessageId = data.messageId;
                } else if (data.type === 'assistant_message_start') {
                  assistantMessageId = data.messageId;
                  onStart(userMessageId, assistantMessageId);
                } else if (data.type === 'chunk') {
                  onChunk(data.content, assistantMessageId);
                } else if (data.type === 'truncated') {
                  if (onTruncated) onTruncated(data.reason || 'unknown', assistantMessageId);
                } else if (data.type === 'done') {
                  resolve();
                  return;
                } else if (data.type === 'error') {
                  if (data.partial && onTruncated) onTruncated('error', assistantMessageId);
                  reject(new Error(data.error));
                  return;
                }
              }
            }
          }

          // If we broke out due to abort, reject with AbortError
          if (signal?.aborted) {
            reject(new DOMException('Stream aborted', 'AbortError'));
          } else {
            resolve();
          }
        } finally {
          if (signal) {
            signal.removeEventListener('abort', abortHandler);
          }
        }
      }).catch(err => {
        if (signal) {
          signal.removeEventListener('abort', abortHandler);
        }
        reject(err);
      });
    });
  }

  async getConversation(conversationId: string): Promise<ChatConversation> {
    const response = await api.get<ChatConversation>(`/api/chat/conversations/${conversationId}`);
    return response;
  }

  async getUserConversations(): Promise<ChatConversation[]> {
    const response = await api.get<ChatConversation[]>('/api/chat/conversations');
    return response;
  }

  async updateConversation(conversationId: string, title: string): Promise<{ success: boolean; title: string }> {
    const response = await api.put<{ success: boolean; title: string }>(`/api/chat/conversations/${conversationId}`, {
      title
    });
    return response;
  }

  async deleteConversation(conversationId: string): Promise<void> {
    await api.delete(`/api/chat/conversations/${conversationId}`);
  }

  async submitFeedback(
    messageId: string,
    conversationId: string,
    feedbackType: 'thumbs_up' | 'thumbs_down',
    comment?: string
  ): Promise<{ success: boolean; feedbackId: string }> {
    const response = await api.post<{ success: boolean; feedbackId: string; message: string }>(
      '/api/chat/feedback',
      {
        messageId,
        conversationId,
        feedbackType,
        comment
      }
    );
    return response;
  }
}

const chatService = new ChatService();
export default chatService;